import { Loader, useNotify, utils, Alert } from '@hotjar/hotsauce';
import { useVideoService } from '@unmoderated/video-service';
import { useEffect, useRef, useState } from 'react';
import { IS_PROD } from '../../constants';
import { AppContainer } from './App.styles';
import { PreviewBanner } from './components/PreviewBanner/PreviewBanner';
import { i18FailureRetryCount, initI18Next, useTranslation } from './i18n';
import { AppRoutes } from './routes/AppRoutes';
import { useProjectParams } from './utils/hooks/useProjectParams';

initI18Next({ useLocalTranslations: !IS_PROD });

export function App() {
  const { ready: translationsReady, i18n } = useTranslation('engage', { useSuspense: false });
  const { t: tUserTest } = useTranslation('user-test');
  const translationFailCount = useRef(0);
  const [hasJoined, setHasJoined] = useState(false);
  const translationFallbackThreshold = i18FailureRetryCount + 1;

  const shouldIgnoreTranslationErrors =
    !translationsReady && translationFailCount.current > translationFallbackThreshold;

  if (!shouldIgnoreTranslationErrors) {
    i18n.off('failedLoading');
    i18n.on('failedLoading', (lng, ns, msg) => {
      translationFailCount.current++;
      // if we reached the failure retry limit, and still got an error
      // we try using local translations instead
      // this a fallback for dev/preview envs, and should not happen on production
      if (translationFailCount.current === translationFallbackThreshold) {
        initI18Next({ useLocalTranslations: true });
      }
      if (translationFailCount.current > translationFallbackThreshold) {
        console.error(`Unable to load ${lng} translations for ${ns}: ${msg}`);
        i18n.off('failedLoading');
      }
    });
  }

  const {
    getParam,
    isLoading: isParamsLoading,
    isError,
    error,
    isFetched,
    isFetching,
  } = useProjectParams();

  const url = getParam('room_url');
  const token = getParam('token');
  const isPreview = !!getParam('is_preview');

  const { joinCall } = useVideoService();
  const { showToast } = useNotify();

  useEffect(() => {
    if (isPreview || hasJoined || isParamsLoading) {
      return;
    }

    if (url && token) {
      setHasJoined(true);
      joinCall({ url, token }).catch((err) => {
        console.error('Error during joining call', err);
      });
    }
  }, [isParamsLoading, url, token, isPreview, hasJoined, joinCall]);

  useEffect(() => {
    if (isError) {
      console.error('Error loading project params', { component: 'App' }, error);
      showToast({ kind: 'danger', text: error.message || '' });
    }
  }, [error, isError, showToast]);

  return (
    <Loader isLoading={!isParamsLoading && !translationsReady && !shouldIgnoreTranslationErrors}>
      <utils.GlobalStyle />
      <AppContainer>
        {isPreview && <PreviewBanner />}
        {!isFetching && !isFetched && (
          <Alert kind="danger">{tUserTest('system-check.alerts.missing-params')}</Alert>
        )}
        <AppRoutes />
      </AppContainer>
    </Loader>
  );
}
